// Query-key + row-timestamp + cache-mutation helpers split out of
// `live-in-club-page.tsx` so the row component can reuse them. The page
// module re-exports these so existing consumers (route loader, tests)
// don't have to switch import paths.

import type {
	ListLiveInClubResult,
	LiveInClubEntry,
	TalentStatusRow,
} from '../../domain/types';

export const liveInClubQueryKey = (clubId: string) =>
	['live-in-club', clubId] as const;

const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
	hour: 'numeric',
	minute: '2-digit',
});
const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat(undefined, {
	month: 'short',
	day: 'numeric',
});

function formatRowTimestamp(entry: LiveInClubEntry): string | null {
	if (entry.status === 'in_club' && entry.liveSinceAt) {
		return `Live since ${TIME_FORMATTER.format(new Date(entry.liveSinceAt))}`;
	}
	if (entry.status === 'not_in_club' && entry.lastInClubAt) {
		return `Last live on ${MONTH_DAY_FORMATTER.format(new Date(entry.lastInClubAt))}`;
	}
	return null;
}

export function resolveRowTimestamp(
	entry: LiveInClubEntry,
	is_mounted: boolean,
): string | null {
	if (!is_mounted) return null;
	return formatRowTimestamp(entry);
}

/**
 * Surgically apply a toggle's server-returned row to the cached
 * `ListLiveInClubResult`. Removes the entry from its current section
 * (matched by id, sweeping both sections), then re-inserts it into the
 * section that matches `newRow.status` with the row response's
 * timestamp fields. The row component preserves `stageName` from the
 * provided `entry` since the server response doesn't carry it.
 *
 * Replaces the previous `invalidateQueries` + refetch path so the
 * section swap happens synchronously on mutation success rather than
 * after the ~1s refetch.
 */
export function applyToggleToCache(
	prev: ListLiveInClubResult | null | undefined,
	entry: LiveInClubEntry,
	newRow: TalentStatusRow,
): ListLiveInClubResult | null {
	if (!prev) return null;

	const remove = (xs: readonly LiveInClubEntry[]) =>
		xs.filter((x) => x.id !== entry.id);

	const newEntry: LiveInClubEntry =
		newRow.status === 'in_club'
			? {
					id: entry.id,
					stageName: entry.stageName,
					status: 'in_club',
					liveSinceAt: newRow.setAt,
					lastInClubAt: null,
				}
			: {
					id: entry.id,
					stageName: entry.stageName,
					status: 'not_in_club',
					liveSinceAt: null,
					lastInClubAt: newRow.lastInClubAt,
				};

	if (newRow.status === 'in_club') {
		return {
			in_club: [...remove(prev.in_club), newEntry],
			not_in_club: remove(prev.not_in_club),
		};
	}
	return {
		in_club: remove(prev.in_club),
		not_in_club: [...remove(prev.not_in_club), newEntry],
	};
}
